import { Slot, Slottable } from '@radix-ui/react-slot';
import { ComponentProps, ReactNode } from 'react';
import { HIDDEN_WHILE_LOADING } from '../Button/buttonClasses/buttonClasses';
import { AppSpinner } from '../AppSpinner/AppSpinner';
import {
  ICON_BUTTON_LOADING_INDICATOR_CLASSES,
  IconButtonColor,
  IconButtonEdge,
  IconButtonSize,
  iconButtonClasses,
} from './iconButtonClasses/iconButtonClasses';

/**
 * A round tap target holding one bare icon and no label.
 *
 * Not a Button variant: this is a circle tinted with `--icon-btn-main`, with an
 * `edge` that lines the *icon* up with the text above. Folding it into Button
 * would add props that only apply without a label.
 *
 * `asChild` renders the child in place of the `<button>`, so links dressed as
 * icon buttons stay real anchors.
 *
 * `loading` matches Button: disabled, icon kept in place but hidden, spinner
 * over it.
 *
 * `Slottable` is needed as on Button: the spinner is a second child even as an
 * unset `loading`'s `false`, and a Slot with two children throws. `asChild`
 * with `loading` makes no sense (a link waits on nothing).
 */
export const IconButton = ({
  size = 'md',
  color = 'default',
  edge,
  loading,
  asChild,
  disabled,
  type = 'button',
  className,
  children,
  ...iconButtonProps
}: {
  size?: IconButtonSize;
  color?: IconButtonColor;
  edge?: IconButtonEdge;
  loading?: boolean;
  asChild?: boolean;
  children?: ReactNode;
} & ComponentProps<'button'>) => {
  const Root = asChild === true ? Slot : 'button';

  return (
    <Root
      type={asChild === true ? undefined : type}
      disabled={loading === true ? true : disabled}
      className={iconButtonClasses({ size, color, edge, className })}
      {...iconButtonProps}
    >
      <Slottable>
        {loading === true ? (
          <span className={HIDDEN_WHILE_LOADING}>{children}</span>
        ) : (
          children
        )}
      </Slottable>
      {loading === true && (
        <span className={ICON_BUTTON_LOADING_INDICATOR_CLASSES}>
          <AppSpinner size='sm' className='text-current' />
        </span>
      )}
    </Root>
  );
};
